import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import {
    Home, Calendar, Dumbbell, CreditCard, LogOut,
    ChevronLeft, ChevronRight, Loader2, FileSignature, MessageSquare, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AthleteTabs from "@/components/parent/AthleteTabs";
import MemberCardGate from "@/components/parent/MemberCardGate";
import WaiverStickyBar from "@/components/parent/WaiverStickyBar";
import { getMissingWaivers } from "@/lib/waiverStatus";
import useMessageSocket from "@/hooks/useMessageSocket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Slow poll — UI primarily updates via WS push. Poll still runs as a
// safety net (e.g. when the socket is bouncing between reconnects).
const UNREAD_POLL_MS = 60000;

/**
 * Tiny hook that maintains the parent's unread-message count, fed by:
 *   1. an initial GET /messages/unread-count
 *   2. live WS events (`message:new` bumps it, `unread:update` overrides
 *      with the authoritative server total)
 *   3. a 60-second safety-net poll
 *
 * Returns 0 for coaches/owners/athlete-mode kids (they don't see the
 * parent nav anyway). Used to badge the Messages nav item.
 */
function useUnreadMessageCount(user) {
    const [count, setCount] = useState(0);
    const { subscribe } = useMessageSocket();

    useEffect(() => {
        if (!user || user.role !== "client") return;
        let alive = true;
        const tick = async () => {
            try {
                const r = await axios.get(`${API}/messages/unread-count`, { withCredentials: true });
                if (alive) setCount(Number(r.data?.unread || 0));
            } catch {
                // Silent — badge just stays at its last value
            }
        };
        Promise.resolve().then(tick);
        const id = setInterval(tick, UNREAD_POLL_MS);
        // Refresh on tab focus so the badge clears quickly after reading.
        const onVis = () => { if (document.visibilityState === "visible") tick(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            alive = false;
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [user]);

    // Live updates from WS — bump on inbound, snap to server total on read.
    useEffect(() => {
        if (!user || user.role !== "client") return undefined;
        return subscribe((evt) => {
            if (evt.type === "message:new" && evt.message?.sender_user_id !== user.user_id && !evt.echo) {
                setCount((c) => c + 1);
            } else if (evt.type === "unread:update") {
                setCount(Number(evt.unread_total || 0));
            }
        });
    }, [subscribe, user]);

    return count;
}

/**
 * Sticky left sidebar for the parent area. Mirrors CoachLayout — but with
 * parent-relevant nav links. Wraps `/dashboard/*` routes via <Outlet />.
 */
export default function ParentLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Treat /dashboard/workouts* AND /dashboard/programs* (the new
    // programs-first flow: TrainingCategories → ProgramsList → ProgramDetail)
    // as "workout routes" so athlete-mode kids don't get bounced back to
    // /athlete when they tap a category card. Without this, clicking
    // "Strength" navigates to /dashboard/programs?category=strength and the
    // effect below sees it as non-workout and replaces the location.
    // Sachin bug 2026-02-28.
    const isWorkoutRoute = (
        location.pathname.startsWith("/dashboard/workouts")
        || location.pathname.startsWith("/dashboard/programs")
    );
    // Athlete-mode kids CAN message the gym (Sachin 2026-08: "make it so
    // sub profiles can message us"). /dashboard/messages renders in the slim
    // athlete chrome instead of bouncing back to /athlete.
    const isMessagesRoute = location.pathname.startsWith("/dashboard/messages");
    const athleteAllowedRoute = isWorkoutRoute || isMessagesRoute;
    const unread = useUnreadMessageCount(user);

    // Waiver gaps across the account (self + each athlete), presence-based.
    // Drives the sidebar pill, the mobile-nav "Sign your waiver" strip, and
    // the desktop sticky bottom bar. Only surfaced once the profile exists
    // (or an athlete is on file) so brand-new mid-wizard accounts aren't
    // nagged before they've even filled anything in.
    const missingWaivers = (!loading && user) ? getMissingWaivers(user) : [];
    const waiverPending = (
        missingWaivers.length > 0
        && (user?.profile_complete || (user?.athletes || []).length > 0)
    );

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        // Coaches/owners shouldn't see this layout — bounce them to their console.
        // Special-case /dashboard/messages so they land on the coach inbox
        // instead of the dashboard root (Sachin spec, 2026-02-13).
        if (user.role === "owner" || user.role === "coach") {
            const dest = location.pathname.startsWith("/dashboard/messages")
                ? "/coach/messages"
                : "/coach";
            navigate(dest, { replace: true });
        }
        // Kids in Athlete Mode get their own slim layout, not the parent one —
        // EXCEPT the workout routes, which they reach from the athlete home.
        if (user.athlete_mode_for && !athleteAllowedRoute) {
            navigate("/athlete", { replace: true });
        }
    }, [loading, user, navigate, athleteAllowedRoute, location.pathname]);

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }
    if (user.role === "owner" || user.role === "coach") return null;
    // Athlete-mode kid on a workout route: render the page without parent chrome
    // (no family tabs, no billing/schedule nav — those are parent-only). Still
    // pin a slim bottom nav so the kid can always get back to Home or jump
    // between Workouts/Schedule. Sachin 2026-02-27: athletes were stuck on
    // workout pages with no way back (no top nav rendered for them either).
    if (user.athlete_mode_for) {
        if (!athleteAllowedRoute) return null;
        return (
            <div data-testid="athlete-workout-layout" className="min-h-screen bg-brand-obsidian text-white pb-20 md:pb-0">
                <Outlet />
                <AthleteModeBottomNav unread={unread} />
            </div>
        );
    }

    // Always surface the Workouts tab in the parent nav. Coaches asked
    // for "easy access — they just tap that to get to workouts." Previously
    // we hid the tab when no family sub + no self-athlete, which made the
    // entry point invisible for new members + multi-kid parents whose
    // family_has_active_sub flag hadn't propagated yet. The /dashboard/workouts
    // page handles the empty/gated states itself.
    const showWorkouts = true;

    const onLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    return (
        <div data-testid="parent-layout" className="min-h-screen bg-brand-obsidian text-white">
            <div className="flex">
                <ParentSidebar
                    onLogout={onLogout}
                    showWorkouts={showWorkouts}
                    waiverPending={waiverPending}
                    unread={unread}
                />
                <main className={`flex-1 min-w-0 pb-20 md:pb-0 ${waiverPending ? "md:pb-24" : ""}`}>
                    <AthleteTabs />
                    <Outlet />
                </main>
            </div>
            <ParentMobileNav
                showWorkouts={showWorkouts}
                waiverPending={waiverPending}
                unread={unread}
            />
            <MemberCardGate />
            <WaiverStickyBar count={waiverPending ? missingWaivers.length : 0} />
        </div>
    );
}

/**
 * ParentMobileNav — sticky bottom tab bar visible only on small screens.
 * Mirrors the desktop sidebar links so athletes/parents can always get to
 * Workouts / Schedule / Billing on a phone. Hidden on md+ where the
 * sidebar takes over.
 */
function ParentMobileNav({ waiverPending, showWorkouts, unread }) {
    const links = [
        { to: "/dashboard", end: true, icon: Home, label: "Home" },
        { to: "/dashboard/schedule", icon: Calendar, label: "Schedule" },
        ...(showWorkouts ? [{ to: "/dashboard/workouts", icon: Dumbbell, label: "Programs" }] : []),
        { to: "/dashboard/messages", icon: MessageSquare, label: "Messages", badge: unread },
        { to: "/dashboard/billing", icon: CreditCard, label: "Billing" },
    ];
    return (
        <nav
            data-testid="parent-mobile-nav"
            className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-zinc-900 bg-black/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
        >
            <div className={links.length === 5 ? "grid grid-cols-5" : "grid grid-cols-4"}>
                {links.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        data-testid={`parent-mobile-link-${l.label.toLowerCase()}`}
                        className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-[0.2em] uppercase transition-colors ${
                            isActive ? "text-brand-red" : "text-zinc-400 hover:text-white"
                        }`}
                    >
                        {({ isActive }) => (
                            <>
                                <span className="relative">
                                    <l.icon className="w-5 h-5" />
                                    {l.badge > 0 && (
                                        <span
                                            data-testid={`parent-mobile-badge-${l.label.toLowerCase()}`}
                                            className="absolute -top-1.5 -right-2 inline-flex items-center justify-center text-[9px] font-bold rounded-full bg-brand-red text-white px-1 min-w-[16px] h-[16px]"
                                        >
                                            {l.badge > 99 ? "99+" : l.badge}
                                        </span>
                                    )}
                                </span>
                                <span>{l.label}</span>
                                {isActive && (
                                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-brand-red" />
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </div>
            {waiverPending && (
                <Link
                    to="/welcome/waiver"
                    data-testid="parent-mobile-waiver-nudge"
                    className="flex items-center justify-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-white font-semibold border-t border-amber-500/40 bg-amber-500/15 hover:bg-amber-500/25 active:bg-amber-500/30 py-2.5 transition-colors"
                >
                    <FileSignature className="w-3.5 h-3.5 text-amber-300" /> Sign your waiver
                    <ArrowRight className="w-3 h-3" />
                </Link>
            )}
        </nav>
    );
}

/**
 * AthleteModeBottomNav — slim sticky bottom tab bar for kids logged into
 * Athlete Mode while they're inside the workout pages. Without this they
 * had no nav whatsoever on those screens — the parent sidebar/mobile-nav
 * is intentionally hidden for athlete-mode users. Sachin 2026-02-27.
 *
 * Renders on all screen sizes (not just mobile) because the parent
 * sidebar that normally fills that role is hidden for athlete-mode.
 */
function AthleteModeBottomNav({ unread = 0 }) {
    const links = [
        { to: "/athlete", end: true, icon: Home, label: "Home" },
        { to: "/dashboard/workouts", icon: Dumbbell, label: "Programs" },
        { to: "/athlete/schedule", icon: Calendar, label: "Schedule" },
        { to: "/dashboard/messages", icon: MessageSquare, label: "Messages", badge: unread },
    ];
    return (
        <nav
            data-testid="athlete-mode-bottom-nav"
            className="fixed bottom-0 left-0 right-0 z-40 border-t border-zinc-900 bg-black/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
        >
            <div className="grid grid-cols-4 max-w-md mx-auto">
                {links.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        data-testid={`athlete-mode-nav-${l.label.toLowerCase()}`}
                        className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-[0.2em] uppercase transition-colors ${
                            isActive ? "text-brand-red" : "text-zinc-400 hover:text-white"
                        }`}
                    >
                        {({ isActive }) => (
                            <>
                                <span className="relative">
                                    <l.icon className="w-5 h-5" />
                                    {Number(l.badge) > 0 && (
                                        <span
                                            data-testid="athlete-mode-messages-badge"
                                            className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-brand-red text-white text-[9px] font-bold flex items-center justify-center leading-none"
                                        >
                                            {l.badge > 99 ? "99+" : l.badge}
                                        </span>
                                    )}
                                </span>
                                <span>{l.label}</span>
                                {isActive && (
                                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-brand-red" />
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </div>
        </nav>
    );
}

function ParentSidebar({ onLogout, waiverPending, showWorkouts, unread }) {    const [collapsed, setCollapsed] = useState(false);

    const links = [
        { to: "/dashboard", end: true, icon: Home, label: "Dashboard" },
        { to: "/dashboard/schedule", icon: Calendar, label: "Schedule" },
        ...(showWorkouts ? [{ to: "/dashboard/workouts", icon: Dumbbell, label: "Programs" }] : []),
        { to: "/dashboard/messages", icon: MessageSquare, label: "Messages", badge: unread },
        { to: "/dashboard/billing", icon: CreditCard, label: "Billing" },
    ];

    return (
        <aside
            data-testid="parent-sidebar"
            className={`hidden md:flex md:flex-col border-r border-zinc-900 bg-black sticky top-20 self-start transition-[width] duration-200 ${
                collapsed ? "w-16" : "w-56"
            }`}
            style={{ height: "calc(100vh - 5rem)" }}
        >
            <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                className="self-end m-2 p-1.5 text-zinc-400 hover:text-white"
            >
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
            <nav className="flex-1 overflow-y-auto px-2 pb-4">
                {links.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        data-testid={`parent-sidebar-link-${l.label.toLowerCase()}`}
                        className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 mb-1 text-sm rounded-md transition-colors ${
                            isActive
                                ? "bg-brand-red/15 text-white border-l-2 border-brand-red"
                                : "text-zinc-200 hover:bg-zinc-900 hover:text-white"
                        } ${collapsed ? "justify-center" : ""}`}
                        title={collapsed ? l.label : undefined}
                    >
                        <span className="relative flex-shrink-0">
                            <l.icon className="w-4 h-4" />
                            {l.badge > 0 && (
                                <span
                                    data-testid={`parent-sidebar-badge-${l.label.toLowerCase()}`}
                                    className="absolute -top-1.5 -right-2 inline-flex items-center justify-center text-[9px] font-bold rounded-full bg-brand-red text-white px-1 min-w-[16px] h-[16px]"
                                >
                                    {l.badge > 99 ? "99+" : l.badge}
                                </span>
                            )}
                        </span>
                        {!collapsed && <span className="truncate flex-1">{l.label}</span>}
                    </NavLink>
                ))}
                {waiverPending && (
                    <Link
                        to="/welcome/waiver"
                        data-testid="parent-sidebar-waiver-nudge"
                        title="Sign your waiver"
                        className={`mt-4 flex items-center gap-2 px-3 py-2.5 text-[10px] tracking-[0.2em] uppercase text-white font-semibold border border-amber-500/40 bg-amber-500/15 hover:bg-amber-500/25 rounded-md transition-colors ${collapsed ? "justify-center" : ""}`}
                    >
                        <FileSignature className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                        {!collapsed && "Sign waiver"}
                    </Link>
                )}
            </nav>
            <div className="border-t border-zinc-900 p-2 space-y-1">
                <button
                    type="button"
                    data-testid="parent-sidebar-logout"
                    onClick={onLogout}
                    title={collapsed ? "Sign out" : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-300 hover:text-brand-red hover:bg-zinc-900 rounded-md ${collapsed ? "justify-center" : ""}`}
                >
                    <LogOut className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && "Sign out"}
                </button>
            </div>
        </aside>
    );
}
